import { AfterViewInit, Component, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { ClaimsListDTO } from '../../mocks/mock';
import { Router } from '@angular/router';
import { Unsubscribe } from '@shared/utility/unsubscribe';
import { DocumentDTO, GetPolicyFilesRes, PolicyListDTO, PolicyType, StatusType } from 'models/DTOs';
import { PolicyService as PoliciesService } from '@services/http/policies.service';
import { Observable, combineLatest, filter, map, take, tap } from 'rxjs';
import { UserService } from 'app/services/user.service';
import { EpicBridgeService } from '@services/http/epic-bridge.service';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DraftService } from '@services/http/draft.service';
import { ClaimsService } from '@services/http/claims.service';
import { convertDateTimeFormat, setDropdownItemsFromNumericEnum, shortenString } from '@shared/utility/helperMethods';
import { NotificationService } from '@services/notification.service';
import { documentNames } from '../../constants/core.constants';
import { AuthService } from '@services/http/auth.service';
import { DropdownItem } from '@shared/components/cinico-dropdown/dropdown.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent extends Unsubscribe implements OnInit, AfterViewInit {
  private _userService: UserService = inject(UserService);
  private _draftsService: DraftService = inject(DraftService);
  private _router: Router = inject(Router);
  private _policiesService: PoliciesService = inject(PoliciesService);
  private _claimsService: ClaimsService = inject(ClaimsService);
  private _epicbridgeService: EpicBridgeService = inject(EpicBridgeService);
  private _notifications: NotificationService = inject(NotificationService);
  private _authService: AuthService = inject(AuthService);
  //private _translate: TranslateService = inject(TranslateService);
  PolicyType = PolicyType;

  @ViewChildren(MatSort) elementSortList: QueryList<MatSort>;

  displayedColumnsPolicies: string[] = [
    'policyTitle',
    'policyNumber',
    'coverageType',
    'coverageDate',
    'policyStatus',
    'amount',
    'nextPayment',
    'actions'
  ];
  displayedColumnsClaims: string[] = ['claimType', 'risk', 'claimNumber', 'status', 'amount', 'accidentDate'];
  displayedColumnsPayments: string[] = ['policyTitle', 'policyID', 'amount', 'nextPayment'];
  userName: string;
  policyTypeEnum = PolicyType;
  statusTypeEnum = StatusType;
  documentNames = documentNames;
  policyStatusDropDown: DropdownItem[] = [];
  policyStatusFilter: DropdownItem = null;

  draftCount$: Observable<number> = this._draftsService.draftSubject.pipe(map(res => res?.maxCount));
  cardPolicies$: Observable<PolicyListDTO[]> = this._policiesService.policiesSubject.pipe(
    tap(items => (this.policySource.data = items)),
    map(items => this.mapCardList(items))
  );
  claims$: Observable<ClaimsListDTO[]> = this._claimsService.claimsSubject.pipe(
    tap(items => (this.claimSource.data = items?.filter(claim => claim.status != 'Approved')))
  );
  // payments$: Observable<PolicyListDTO[]> = this._policiesService.policiesSubject.pipe(
  //   tap(items => (this.paymentSource.data = items))
  // );
  profile$: Observable<string> = this._userService.userSubject.pipe(
    map(value => (value ? `${value?.firstName} ${value.lastName}` : ''))
  );
  policySource = new MatTableDataSource<PolicyListDTO>();
  claimSource = new MatTableDataSource<ClaimsListDTO>();
  paymentSource = new MatTableDataSource<PolicyListDTO>();

  ngOnInit() {
    this.policyStatusDropDown = setDropdownItemsFromNumericEnum(this.statusTypeEnum);
    this.policyStatusDropDown = this.policyStatusDropDown.filter(i => i.value !== 'InForce');
    this.policySource.filterPredicate = this.statusFilterPredicate;
    const user = this._userService.userSubject.value;

    if (user) {
      if (!this._policiesService.policiesSubject.value?.length) this._policiesService.getPolicies();
      combineLatest([
        this._policiesService.policiesSubject.pipe(
          filter(p => !!p && p.length > 0),
          take(1)
        )
      ]).subscribe(([policies]) => {
        this._claimsService.getAllClaims(user, policies);
      });
    }

    this.enroll2FA();

    this.draftCount$ = this._draftsService.getDraftCount();
  }

  enrolled2FA: boolean = false;
  async enroll2FA() {
    // Check if user is already enrolled
    this.enrolled2FA = await this._authService.checkIfUserHasEnrolled2FA();
    if (this.enrolled2FA) {
      return;
    }

    const maxAttempts = 5;
    let attempts = 0;
    let success = false;

    // Log user out automatically after 5 wrong attempts:
    while (attempts < maxAttempts && !success) {
      success = await this._authService.enrollMfa(() => this._authService.logout());
      attempts++;
    }

    if (!success) {
      // const message = this._translate.instant('keywords.2FA_setup_failed_after_5_attempts');
      // this._notifications.showMessage(message, 5000);
    } else {
      this.enrolled2FA = true;
    }
  }

  ngAfterViewInit() {
    const array = this.elementSortList.toArray();
    this.policySource.sort = array[0];
    this.claimSource.sort = array[1];
    this.paymentSource.sort = array[2];
  }

  mapCardList(list: PolicyListDTO[]) {
    const nullPolicy: PolicyListDTO = {
      policyNumber: 0,
      policyID: '',
      policyTitle: '',
      policyType: PolicyType.Home,
      policyStatus: '',
      coverageType: '',
      coverageDateFrom: '',
      coverageDateTo: '',
      amount: 0,
      nextPayment: ''
    };

    const healthPolicy = list?.find(
      item => item.policyType === PolicyType.Health && item.policyStatus === StatusType[StatusType.Active]
    ) ?? {
      ...nullPolicy,
      policyType: PolicyType.Health,
      policyTitle: 'Health Policy'
    };

    const motorPolicy = list?.find(
      item =>
        item.policyType === PolicyType.Motor && item.policyStatus.replace(' ', '') === StatusType[StatusType.InForce]
    ) ?? {
      ...nullPolicy,
      policyType: PolicyType.Motor,
      policyTitle: 'Motor Policy'
    };

    const homePolicy = list?.find(
      item =>
        item.policyType === PolicyType.Home && item.policyStatus.replace(' ', '') === StatusType[StatusType.InForce]
    ) ?? {
      ...nullPolicy,
      policyType: PolicyType.Home,
      policyTitle: 'Home Policy'
    };

    return [healthPolicy, motorPolicy, homePolicy];
  }

  getQuote(policyType: PolicyType) {
    switch (policyType) {
      case PolicyType.Home: {
        this._router.navigate(['quotes/create-quote/home-quote']);
        break;
      }
      case PolicyType.Motor: {
        this._router.navigate(['quotes/create-quote/motor-quote']);
        break;
      }
      case PolicyType.Health: {
        this._router.navigate(['quotes/create-quote/health-quote']);
        break;
      }
    }
  }

  getIcon(policyType: PolicyType) {
    return this._policiesService.getIcon(policyType);
  }

  getPolicyActivityStatusHTML(status: string) {
    const statusPrep = status.replace(' ', '');

    let chipClass = 'status-chip-warn';
    let chipText = status;

    switch (StatusType[statusPrep]) {
      case StatusType.Active: {
        (chipClass = 'status-chip-success'), (chipText = '● Active');
        break;
      }
      case StatusType.InForce: {
        (chipClass = 'status-chip-success'), (chipText = '● Active');
        break;
      }
      case StatusType.Review: {
        (chipClass = 'status-chip-review'), (chipText = '● In Review');
        break;
      }
      case StatusType.ExpiringSoon: {
        (chipClass = 'status-chip-warn'), (chipText = '● Expiring Soon');
        break;
      }
      case StatusType.Inactive: {
        (chipClass = 'status-chip-secondary'), (chipText = '● Inactive');
        break;
      }
      case StatusType.Lapsed: {
        (chipClass = 'status-chip-fail'), (chipText = '● Lapsed');
        break;
      }
      case StatusType.Cancelled: {
        (chipClass = 'status-chip-secondary'), (chipText = '● Cancelled');
        break;
      }
    }

    return `<div class="status-chip-container">
              <div class="${chipClass}">
                <span>${chipText}</span>
              </div>
            </div>`;
  }

  getClaimActivityStatusHTML(status: string) {
    let chipClass = 'status-chip-warn';
    let chipText = status;

    if (status.includes('Awaiting')) {
      chipClass = 'status-chip-review';
      chipText = `● ${status}`;
    } else if (status === '') {
      chipText = `N/A`;
    }

    return `<div class="status-chip-container">
              <div class="${chipClass}">
                <span>${chipText}</span>
              </div>
            </div>`;
  }

  shortenString(fullID: string, length: number): string {
    return shortenString(fullID, length);
  }

  convertDateTimeFormat(datetimeString: string): string {
    return convertDateTimeFormat(datetimeString);
  }

  menuItemClicked(policy: PolicyListDTO, documentType: string) {
    const obsFiles: Observable<GetPolicyFilesRes[]> = this._epicbridgeService.getPolicyFiles(policy.policyID);
    this.sub = obsFiles.subscribe(files => {
      if (files) {
        const fileID: string = files.find(file => file.documentType.includes(documentType))?.fileId;
        if (!fileID) {
          this._notifications.showMessage(`No matching file found for document type: ${documentType}`);
          return;
        }

        const obsDocument: Observable<DocumentDTO> = this._epicbridgeService.getFileLinkData(fileID);
        obsDocument.subscribe(file => {
          if (file.data === null) {
            this._notifications.showMessage('EB Error: File found, but no file data recieved!');
            return;
          }
          const fileBlob = this._epicbridgeService.createEBFileBlob(file);
          const blobUrl = URL.createObjectURL(fileBlob);

          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = file.fileName;
          a.click();

          URL.revokeObjectURL(blobUrl);
        });
      }
    });
  }

  navigateToPolicyDetails(policy: PolicyListDTO) {
    if (policy.coverageDateTo) {
      this._router.navigate(['policy/policy-details', policy.policyNumber]);
    }
  }

  statusFilterPredicate(data: PolicyListDTO, filter: string) {
    if (!filter || filter === 'Select any') {
      return true;
    }

    switch (filter) {
      case 'InReview':
        filter = 'In Review';
        break;
      case 'ExpiringSoon':
        filter = 'Expiring Soon';
        break;
    }

    if (filter === 'Active') return data.policyStatus === 'In Force' || data.policyStatus === 'Active';
    return data.policyStatus === filter;
  }

  filterStatus(filter: string) {
    this.policySource.filter = filter;
  }
}
